// @flow
import {
    MAP_HIGHLIGHT_PIN,
    MAP_CLEAR_HIGHLIGHTED_PINS,
    MAP_CHANGE_HIT_PREVIEW,
    MAP_LOADED,
    MAP_IDLE,
    MAP_UPDATE,
    MAP_RESET_SEARCH_BUTTON,
    MAP_SYNC_BOUNDS,
    MAP_ENABLE_SEARCH_ON_MOVE,
    MAP_DISABLE_SEARCH_ON_MOVE,
    MAP_ADD_MARKER_CLICKED,
    MAP_SHOW_SEARCH_BUTTON,
    MAP_SET_MOVE_STATE,
    MAP_RESET_TO_INITIAL_BOUNDS
} from '../actions/map';
import {isEqual, isNaN, pick} from 'lodash';
import {BOUNDING_BOX_PARAMS} from '../constants/map';
import {WORLD_SEARCH_BOUNDS} from '../constants/map';
import {SEARCH_REQUEST_QUERY_COMPLETE} from '../actions/searchRequestQuery';

import type {Action} from '../actions/map';
import type {MapViewport} from '../components/Map/Map';
import type {SearchRequestQueryCompleteAction} from '../actions/searchRequestQuery';
import type {MapCentralPin} from '../components/Map/Map';
import type {Geography} from '../queries/searchRequestQuery';

/**
 * a canonical way to build a mapViewport
 * Why needed? If mapViewport is non-null from data, the __typename metadata attribute is appended to data
 * we use pick in the event that any other metadata attributes ride along with graphql data
 */
export const canonicalizeMapViewport = (
    mapViewport: ?MapViewport
): ?MapViewport => {
    let result = mapViewport && pick(mapViewport, BOUNDING_BOX_PARAMS);
    return Object.keys(result || {}).length ? result : null;
};

/**
 * Returns MapCentralPin if location is present and coordinates are valid
 *
 * @param geography
 * @returns {MapCentralPin|null}
 */
const returnValidCentralPin = (geography: ?Geography): ?MapCentralPin => {
    if (!geography) return null;
    const {location} = geography;
    const lat = parseFloat(location && location.latitude);
    const lng = parseFloat(location && location.longitude);

    if (!isNaN(lat) && lat !== -1 && !isNaN(lng) && lng !== -1) {
        return {lat, lng};
    }

    return null;
};

export type State = {
    +bounds: ?MapViewport,
    +initialBounds: ?MapViewport,
    +currentHitPrev: ?string,
    +isSearchButtonVisible?: boolean,
    +listingId: ?string,
    +mapCentralPin: ?MapCentralPin,
    +mapHasLoaded?: boolean,
    +mapMarkersClicked: Object,
    +mapMoveState?: boolean,
    +shouldDisableSearchButton?: boolean,
    +shouldSearchOnMove: boolean,
    +shouldShowSearchButton?: boolean,
    +fitBoundsOverride: boolean,
    +mapOptions?: Object,
    +zoom: number
};

const INITIAL_SEARCHBUTTON = {
    shouldShowSearchButton: false,
    isSearchButtonVisible: false,
    mapMoveState: false,
    shouldDisableSearchButton: false,
    shouldSearchOnMove: false
};

const initialState: State = {
    listingId: null,
    currentHitPrev: null,
    initialBounds: null,
    bounds: null,
    /* although defaulted, zoom is determined by size of map
    window and the result of fitBounds call in AsyncGoogleMap.js */
    zoom: 0,
    mapCentralPin: null,
    mapHasLoaded: false,
    mapMarkersClicked: {},
    fitBoundsOverride: false,
    ...INITIAL_SEARCHBUTTON
};

const DISABLE_SEARCHBUTTON = {
    isSearchButtonVisible: true,
    shouldDisableSearchButton: true,
    shouldShowSearchButton: true
};

export const updateMapViewport = (
    state: State,
    {payload}: SearchRequestQueryCompleteAction
) => {
    const {error, results} = payload;

    let incomingMapViewport = canonicalizeMapViewport(
        results && results.mapViewport
    );
    if (
        !incomingMapViewport &&
        !isEqual(state.bounds, WORLD_SEARCH_BOUNDS) &&
        (error || !state.bounds)
    ) {
        return {
            initialBounds: {...WORLD_SEARCH_BOUNDS},
            bounds: {...WORLD_SEARCH_BOUNDS},
            zoom: 1
        };
    } else if (
        incomingMapViewport &&
        !isEqual(state.bounds, incomingMapViewport)
    ) {
        // user performs a search which changes the incoming viewport
        return {
            initialBounds: {...incomingMapViewport},
            bounds: {...incomingMapViewport}
        };
    } else if (
        incomingMapViewport &&
        !isEqual(state.initialBounds, incomingMapViewport)
    ) {
        return {
            initialBounds: {...incomingMapViewport}
        };
    }

    return {};
};

export const updateMapCentralPin = (
    state: State,
    {payload}: SearchRequestQueryCompleteAction
): Object => {
    const {results} = payload;
    const mapCentralPin = returnValidCentralPin(results && results.geography);
    const shouldShowMapCentralPin =
        results &&
        results.geography &&
        results.geography.shouldShowMapCentralPin;
    const centralPinUpdate = shouldShowMapCentralPin ? mapCentralPin : null;

    if (!mapCentralPin || isEqual(state.mapCentralPin, centralPinUpdate)) {
        return {}; // no op
    }

    return {
        mapCentralPin: centralPinUpdate
    };
};

export default function reducer(
    state: State = initialState,
    action: Action | SearchRequestQueryCompleteAction
): State {
    switch (action.type) {
        case MAP_HIGHLIGHT_PIN:
            return {
                ...state,
                listingId: action.payload.listingId
            };
        case MAP_CLEAR_HIGHLIGHTED_PINS:
            return {
                ...state,
                listingId: null
            };
        case MAP_ADD_MARKER_CLICKED:
            let updatedMapMarkersClicked = {...state.mapMarkersClicked};
            updatedMapMarkersClicked[action.payload.listingId] = {};
            return {
                ...state,
                mapMarkersClicked: updatedMapMarkersClicked
            };
        case MAP_CHANGE_HIT_PREVIEW:
            return {
                ...state,
                shouldShowSearchButton: false,
                currentHitPrev: action.payload.currentHitPrev
            };
        case MAP_LOADED:
            return {
                ...state,
                zoom: action.payload.zoom,
                mapHasLoaded: true,
                fitBoundsOverride: false
            };
        case MAP_SHOW_SEARCH_BUTTON:
            return {...state, ...DISABLE_SEARCHBUTTON};
        case MAP_IDLE: {
            let newState: Object = {
                shouldDisableSearchButton: false,
                fitBoundsOverride: false
            };

            if (state.shouldShowSearchButton) {
                newState.shouldShowSearchButton = false;
                newState.isSearchButtonVisible = true;
            }
            return {...state, ...newState};
        }
        case MAP_UPDATE: {
            const {bounds, zoom} = action.payload;

            return {
                ...state,
                bounds,
                zoom,
                shouldShowSearchButton: true
            };
        }
        case MAP_SYNC_BOUNDS: {
            const {bounds, mapOptions} = action.payload;
            let incomingBounds = canonicalizeMapViewport(bounds);
            return {
                ...state,
                initialBounds: incomingBounds,
                bounds: incomingBounds,
                ...mapOptions
            };
        }
        case MAP_RESET_SEARCH_BUTTON: {
            return {...state, ...INITIAL_SEARCHBUTTON};
        }
        case MAP_ENABLE_SEARCH_ON_MOVE: {
            return {...state, shouldSearchOnMove: true};
        }
        case MAP_DISABLE_SEARCH_ON_MOVE: {
            return {...state, shouldSearchOnMove: false};
        }
        case MAP_SET_MOVE_STATE: {
            return {...state, mapMoveState: action.payload.mapMoveState};
        }
        case SEARCH_REQUEST_QUERY_COMPLETE: {
            const viewport = updateMapViewport(state, action);
            const mapCentralPin = updateMapCentralPin(state, action);

            return {
                ...state,
                ...viewport,
                ...mapCentralPin
            };
        }
        case MAP_RESET_TO_INITIAL_BOUNDS: {
            return {...state, bounds: state.initialBounds};
        }
        default:
            // Ensures we have handled every single action type
            // by casting the action to `empty` in the default case.
            // If Flow finds it possible to reach this case, the empty
            // cast will fail.
            (action: empty); // eslint-disable-line no-unused-expressions
            return state;
    }
}
